import { useState, useCallback } from 'react';
import { TextField, Checkbox, Select, Button, Banner } from '@shopify/polaris';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchSettings,
  updateSettings,
  fetchJitsuCredentials,
  saveJitsuCredentials,
  deleteJitsuCredentials,
  type ShopSettings,
} from '../utils/api';

export default function SettingsPage() {
  const queryClient = useQueryClient();
  const [formDirty, setFormDirty] = useState(false);
  const [jitsuApiKey, setJitsuApiKey] = useState('');
  const [jitsuKeyDirty, setJitsuKeyDirty] = useState(false);

  const { data: settings, isLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings,
  });

  const { data: jitsuCredentials, isLoading: isLoadingCredentials } = useQuery({
    queryKey: ['jitsuCredentials'],
    queryFn: fetchJitsuCredentials,
  });

  const [formData, setFormData] = useState<Partial<ShopSettings>>({});
  const currentSettings = { ...settings, ...formData };

  const mutation = useMutation({
    mutationFn: updateSettings,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['settings'] });
      setFormDirty(false);
      setFormData({});
    },
  });

  const jitsuSaveMutation = useMutation({
    mutationFn: saveJitsuCredentials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jitsuCredentials'] });
      setJitsuApiKey('');
      setJitsuKeyDirty(false);
    },
  });

  const jitsuDeleteMutation = useMutation({
    mutationFn: deleteJitsuCredentials,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['jitsuCredentials'] });
    },
  });

  const handleChange = useCallback((field: keyof ShopSettings, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setFormDirty(true);
  }, []);

  const handleSave = useCallback(() => {
    mutation.mutate(currentSettings as ShopSettings);
  }, [currentSettings, mutation]);

  const handleSaveJitsuKey = useCallback(() => {
    if (jitsuApiKey) {
      jitsuSaveMutation.mutate(jitsuApiKey);
    }
  }, [jitsuApiKey, jitsuSaveMutation]);

  const handleDeleteJitsuKey = useCallback(() => {
    jitsuDeleteMutation.mutate();
  }, [jitsuDeleteMutation]);

  if (isLoading || isLoadingCredentials) {
    return (
      <div className="jitsu-card">
        <p>Loading settings...</p>
      </div>
    );
  }

  return (
    <div>
      {/* Notifications */}
      {mutation.isSuccess && (
        <div style={{ marginBottom: '16px' }}>
          <Banner tone="success" onDismiss={() => mutation.reset()}>
            Settings saved successfully.
          </Banner>
        </div>
      )}
      {jitsuSaveMutation.isSuccess && (
        <div style={{ marginBottom: '16px' }}>
          <Banner tone="success" onDismiss={() => jitsuSaveMutation.reset()}>
            Jitsu API key saved successfully.
          </Banner>
        </div>
      )}

      {/* Jitsu API Key Section */}
      <div className="jitsu-card">
        <h3 className="jitsu-card-title">Jitsu API Key</h3>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          Enter your Jitsu API key to connect your store to Jitsu for shipping.
        </p>

        {jitsuCredentials?.hasCredentials ? (
          <div>
            <div style={{ marginBottom: '12px' }}>
              <span style={{ color: 'var(--jitsu-grey)' }}>Current API Key: </span>
              <strong>{jitsuCredentials.jitsuApiKey}</strong>
            </div>
            <Button
              variant="primary"
              tone="critical"
              onClick={handleDeleteJitsuKey}
              loading={jitsuDeleteMutation.isPending}
            >
              Remove API Key
            </Button>
          </div>
        ) : (
          <div style={{ maxWidth: '400px' }}>
            <div style={{ marginBottom: '12px' }}>
              <TextField
                label="API Key"
                type="password"
                value={jitsuApiKey}
                onChange={(value) => {
                  setJitsuApiKey(value);
                  setJitsuKeyDirty(true);
                }}
                autoComplete="off"
                placeholder="Enter your Jitsu API key"
                helpText="Your Jitsu API key (Token) for authentication"
              />
            </div>
            <Button
              variant="primary"
              onClick={handleSaveJitsuKey}
              disabled={!jitsuKeyDirty || !jitsuApiKey}
              loading={jitsuSaveMutation.isPending}
            >
              Save API Key
            </Button>
          </div>
        )}
      </div>

      {/* Shipping Settings */}
      <div className="jitsu-card">
        <div className="jitsu-card-header">
          <h3 className="jitsu-card-title">Shipping Settings</h3>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={!formDirty || mutation.isPending}
            loading={mutation.isPending}
          >
            Save Settings
          </Button>
        </div>

        <div style={{ maxWidth: '400px' }}>
          <div style={{ marginBottom: '16px' }}>
            <Select
              label="Default Carrier"
              options={[
                { label: 'Jitsu Standard', value: 'jitsu_standard' },
                { label: 'Jitsu Express', value: 'jitsu_express' },
                { label: 'Jitsu Economy', value: 'jitsu_economy' },
              ]}
              value={currentSettings.defaultCarrier ?? 'jitsu_standard'}
              onChange={(value) => handleChange('defaultCarrier', value)}
            />
          </div>
          <div style={{ marginBottom: '12px' }}>
            <Checkbox
              label="Auto-create shipments"
              checked={currentSettings.autoFulfill ?? false}
              onChange={(value) => handleChange('autoFulfill', value)}
              helpText="Automatically create shipments when orders are ready to ship"
            />
          </div>
          <div>
            <Checkbox
              label="Notify customers on delivery"
              checked={currentSettings.notifyOnDelivery ?? true}
              onChange={(value) => handleChange('notifyOnDelivery', value)}
              helpText="Send customer notification when package is delivered"
            />
          </div>
        </div>
      </div>

      {/* Origin Address */}
      <div className="jitsu-card">
        <div className="jitsu-card-header">
          <h3 className="jitsu-card-title">Origin Address</h3>
        </div>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          The address where shipments will be picked up from.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', maxWidth: '600px' }}>
          <div style={{ gridColumn: '1 / -1' }}>
            <TextField
              label="Company/Warehouse Name"
              value={currentSettings.originName ?? ''}
              onChange={(value) => handleChange('originName', value)}
              autoComplete="organization"
            />
          </div>
          <div style={{ gridColumn: '1 / -1' }}>
            <TextField
              label="Street Address"
              value={currentSettings.originAddress1 ?? ''}
              onChange={(value) => handleChange('originAddress1', value)}
              autoComplete="street-address"
            />
          </div>
          <TextField
            label="City"
            value={currentSettings.originCity ?? ''}
            onChange={(value) => handleChange('originCity', value)}
            autoComplete="address-level2"
          />
          <TextField
            label="State"
            value={currentSettings.originState ?? ''}
            onChange={(value) => handleChange('originState', value)}
            autoComplete="address-level1"
            placeholder="e.g., CA"
          />
          <TextField
            label="ZIP Code"
            value={currentSettings.originZip ?? ''}
            onChange={(value) => handleChange('originZip', value)}
            autoComplete="postal-code"
          />
          <Select
            label="Timezone"
            options={[
              { label: 'Eastern (ET)', value: 'America/New_York' },
              { label: 'Central (CT)', value: 'America/Chicago' },
              { label: 'Mountain (MT)', value: 'America/Denver' },
              { label: 'Pacific (PT)', value: 'America/Los_Angeles' },
            ]}
            value={currentSettings.timezone ?? 'America/New_York'}
            onChange={(value) => handleChange('timezone', value)}
          />
        </div>
      </div>

      {/* Default Package Dimensions */}
      <div className="jitsu-card">
        <div className="jitsu-card-header">
          <h3 className="jitsu-card-title">Default Package Dimensions</h3>
        </div>
        <p style={{ color: 'var(--jitsu-mid-grey)', marginBottom: '16px' }}>
          Default box dimensions used for rate calculations and shipment creation. These can be overridden per shipment.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', maxWidth: '600px' }}>
          <TextField
            label="Length"
            type="number"
            value={String(currentSettings.defaultPackageLength ?? 12)}
            onChange={(value) => handleChange('defaultPackageLength', parseFloat(value) || 0)}
            autoComplete="off"
            suffix={currentSettings.defaultDimensionUnit ?? 'in'}
          />
          <TextField
            label="Width"
            type="number"
            value={String(currentSettings.defaultPackageWidth ?? 10)}
            onChange={(value) => handleChange('defaultPackageWidth', parseFloat(value) || 0)}
            autoComplete="off"
            suffix={currentSettings.defaultDimensionUnit ?? 'in'}
          />
          <TextField
            label="Height"
            type="number"
            value={String(currentSettings.defaultPackageHeight ?? 8)}
            onChange={(value) => handleChange('defaultPackageHeight', parseFloat(value) || 0)}
            autoComplete="off"
            suffix={currentSettings.defaultDimensionUnit ?? 'in'}
          />
          <Select
            label="Unit"
            options={[
              { label: 'Inches', value: 'in' },
              { label: 'Centimeters', value: 'cm' },
            ]}
            value={currentSettings.defaultDimensionUnit ?? 'in'}
            onChange={(value) => handleChange('defaultDimensionUnit', value)}
          />
        </div>
      </div>
    </div>
  );
}

